import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CreditCard, Clipboard, Calculator, Download, History } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bloc « Souscription RCDS » de la fiche membre (invitation IA).
export default function MembreSouscriptionRCDS({ selectedMembre, setSelectedMembre, getAuthHeader, fetchMembres }) {
  const [editMontant, setEditMontant] = useState('');
  const [busy, setBusy] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const sous = selectedMembre?.souscription_rcds;
  if (!sous) return null;
  const payee = sous.paiement_valide || sous.statut === 'validee';

  const saveMontant = async () => {
    const v = Number(editMontant);
    if (!v || v <= 0) { toast.error('Montant HT invalide'); return; }
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/member-invitation/admin/membres/${selectedMembre.id}/simulateur-montant`, { montant_ht: v }, { headers: getAuthHeader() });
      const est = data.estimation || {};
      const updated = { ...selectedMembre, simulateur_rcds: { ...(selectedMembre.simulateur_rcds || {}), montant_ht: est.montant_ht, estimation: est, historique_montants: data.historique || selectedMembre.simulateur_rcds?.historique_montants }, souscription_rcds: { ...sous, montant_ht: est.montant_ht, montant_ttc: est.total_ttc } };
      setSelectedMembre(updated);
      setEditMontant('');
      toast.success(`Montant mis à jour : ${est.total_ttc} € TTC`);
      if (fetchMembres) fetchMembres();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API}/member-invitation/admin/membres/${selectedMembre.id}/souscription/pdf`, { headers: getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `recap_souscription_${sous.numero_police || selectedMembre.numero_membre}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="rounded-xl border border-violet-300 bg-violet-50 p-4" data-testid="membre-souscription-rcds">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-violet-800 flex items-center gap-2">
          <CreditCard className="h-4 w-4" /> Souscription RCDS
        </h4>
        {payee ? (
          <Badge className="bg-emerald-500 text-white border-0" data-testid="membre-souscription-badge">Payée</Badge>
        ) : (
          <Badge className="bg-amber-500 text-white border-0" data-testid="membre-souscription-badge">À finaliser</Badge>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div><p className="text-[10px] uppercase text-violet-600">N° police</p><p className="font-medium">{sous.numero_police || '—'}</p></div>
        <div><p className="text-[10px] uppercase text-violet-600">Montant HT</p><p className="font-medium">{sous.montant_ht || '—'} €</p></div>
        <div><p className="text-[10px] uppercase text-violet-600">Montant TTC</p><p className="font-medium">{sous.montant_ttc || '—'} €</p></div>
        <div><p className="text-[10px] uppercase text-violet-600">Statut</p><p className="font-medium capitalize">{sous.statut || 'brouillon'}</p></div>
      </div>
      {!payee && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input type="number" min="0" value={editMontant} onChange={(e) => setEditMontant(e.target.value)} placeholder="Nouveau montant HT (€)" className="h-9 w-48 rounded-md border border-violet-300 px-3 text-sm" data-testid="membre-edit-montant-input" />
          <Button size="sm" variant="outline" className="border-violet-400 text-violet-700" disabled={busy} onClick={saveMontant} data-testid="membre-edit-montant-btn">
            <Calculator className="h-4 w-4 mr-1" /> {busy ? 'Calcul…' : 'Recalculer'}
          </Button>
        </div>
      )}
      {Array.isArray(selectedMembre.simulateur_rcds?.historique_montants) && selectedMembre.simulateur_rcds.historique_montants.length > 0 && (
        <div className="mt-3 rounded-lg border border-violet-200 bg-white/60 p-2.5" data-testid="membre-historique-montants">
          <p className="text-[10px] uppercase text-violet-600 font-semibold mb-1.5 flex items-center gap-1"><History className="h-3 w-3" /> Historique des montants</p>
          <ul className="space-y-1 text-xs text-slate-600">
            {[...selectedMembre.simulateur_rcds.historique_montants].reverse().map((h, i) => (
              <li key={i} className="flex flex-wrap items-center gap-x-2">
                <span className="text-slate-400">{h.modifie_le ? new Date(h.modifie_le).toLocaleString('fr-FR') : '—'}</span>
                <span>HT {h.ancien_ht ?? '—'} € → <strong>{h.nouveau_ht ?? '—'} €</strong> (TTC {h.ancien_ttc ?? '—'} € → <strong>{h.nouveau_ttc ?? '—'} €</strong>)</span>
                <span className="text-violet-600">par {h.modifie_par_email || '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="border-slate-300 text-slate-700" disabled={downloadingPdf} onClick={downloadPdf} data-testid="membre-souscription-pdf-btn">
          <Download className="h-4 w-4 mr-1" /> {downloadingPdf ? 'Génération…' : 'Télécharger le PDF'}
        </Button>
        {!payee && sous.lien_paiement && (
          <>
            <Button size="sm" className="bg-violet-600 text-white" onClick={() => window.open(sous.lien_paiement, '_blank')} data-testid="membre-souscrire-btn">
              <CreditCard className="h-4 w-4 mr-1" /> Souscrire (payer par carte)
            </Button>
            <Button size="sm" variant="outline" className="border-violet-300 text-violet-700" onClick={() => { navigator.clipboard.writeText(sous.lien_paiement); toast.success('Lien de paiement copié'); }} data-testid="membre-souscription-copier-lien">
              <Clipboard className="h-4 w-4 mr-1" /> Copier le lien
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
